<script lang="ts" setup>
import { Ref } from "vue";

let vMaska: unknown | undefined;

if (!import.meta.env.SSR) {
  const maska = await import("maska");
  vMaska = maska.vMaska;
}

const props = withDefaults(
  defineProps<{
    title?: string;
    placeholder?: string;
    modelValue: string;
    type?: "text" | "password" | "textarea";
    error?: string | Ref<string>;
    mask?: {
      mask: string;
      tokens?: string;
    };
    disabled?: boolean;
    resize?: boolean;
  }>(),
  {
    title: undefined,
    placeholder: undefined,
    type: "text",
    error: "",
    mask: undefined,
    disabled: false,
    resize: true,
  },
);

const emit = defineEmits(["update:modelValue"]);
const maskDetails = {
  masked: "",
  unmasked: "",
  completed: false,
};

const onInput = (event: Event) => {
  emit("update:modelValue", (event.target as HTMLInputElement).value);
};
</script>

<template>
  <div class="input-group">
    <textarea
      v-if="props.type === 'textarea'"
      :value="props.modelValue"
      :class="{ error: props.error, 'disable-resize': !props.resize }"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      @input="onInput"
    />
    <input
      v-else-if="props.mask"
      v-maska="maskDetails"
      :class="{ error: props.error }"
      :value="props.modelValue"
      :placeholder="props.placeholder"
      :type="props.type"
      :data-maska="props.mask.mask"
      :data-maska-tokens="props.mask.tokens"
      :disabled="props.disabled"
      @input="onInput"
    />
    <input
      v-else
      :class="{ error: props.error }"
      :value="props.modelValue"
      :placeholder="props.placeholder"
      :type="props.type"
      :disabled="props.disabled"
      @input="onInput"
    />
    <label v-if="props.title" class="input-group-label">{{
      props.title
    }}</label>
    <span v-if="props.error" class="error-text">{{ props.error }}</span>
  </div>
</template>

<style lang="scss" scoped>
@import "~/assets/scss/_media";

.input-group {
  position: relative;
  padding-top: var(--input-size, 15px);

  font-family: "Inter", sans-serif;
}

textarea {
  height: 100%;
  resize: vertical;
  font-family: "Inter", sans-serif !important;
}

.disable-resize {
  resize: none !important;
}

input,
textarea {
  width: 100%;
  box-sizing: border-box;

  padding-block: 15px;
  padding-inline: 25px;

  font-size: var(--input-size, 15px);
  border-radius: 12px;
  border: 1px solid #acacac;
  outline: none;

  transition: 0.25s border ease-in-out;

  background-color: var(--bg-color, #fff);

  &:focus {
    border: 1px solid var(--color-accent);

    & ~ label {
      color: var(--color-accent);
    }
  }

  &.error {
    border: 1px solid var(--color-error);

    & ~ label {
      color: var(--color-error);
    }
  }
}

label {
  color: #acacac;

  background-color: var(--bg-color, #fff);

  position: absolute;
  top: 3px;
  left: 15px;

  padding-inline: 10px;

  font-size: 14px;

  transition: 0.25s color ease-in-out;

  @include media("max", "md") {
    font-size: 12px;
  }
}

.error-text {
  color: var(--color-error);
  padding-left: 25px;
  font-size: 12px;
  padding-top: 10px;
}
</style>
